export function searchTextScore(value: string, query: string) {
  const normalizedValue = normalizeSearchText(value);
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) {
    return Number.POSITIVE_INFINITY;
  }

  if (normalizedValue === normalizedQuery) {
    return 0;
  }

  if (normalizedValue.startsWith(normalizedQuery)) {
    return 1 + (normalizedValue.length - normalizedQuery.length) / 1_000;
  }

  const substringIndex = normalizedValue.indexOf(normalizedQuery);
  if (substringIndex >= 0) {
    return 2 + substringIndex / 100;
  }

  const valueTokens = normalizedValue.split(" ").filter(Boolean);
  const queryTokens = normalizedQuery.split(" ").filter(Boolean);
  let tokenScore = 0;

  for (const queryToken of queryTokens) {
    let bestTokenScore = Number.POSITIVE_INFINITY;

    for (const valueToken of valueTokens) {
      if (valueToken === queryToken) {
        bestTokenScore = 0;
        break;
      }

      if (valueToken.startsWith(queryToken)) {
        bestTokenScore = Math.min(bestTokenScore, 0.5);
        continue;
      }

      if (valueToken.includes(queryToken)) {
        bestTokenScore = Math.min(bestTokenScore, 1);
        continue;
      }

      const tolerance = queryToken.length >= 7 ? 2 : queryToken.length >= 4 ? 1 : 0;
      if (tolerance > 0) {
        const distance = levenshteinDistance(valueToken, queryToken);
        if (distance <= tolerance) {
          bestTokenScore = Math.min(bestTokenScore, 2 + distance);
        }
      }
    }

    if (!Number.isFinite(bestTokenScore)) {
      return Number.POSITIVE_INFINITY;
    }

    tokenScore += bestTokenScore;
  }

  return 10 + tokenScore;
}

export function normalizeSearchText(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function levenshteinDistance(first: string, second: string) {
  if (isAdjacentTransposition(first, second)) {
    return 1;
  }

  const previous = Array.from({ length: second.length + 1 }, (_, index) => index);

  for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
    let diagonal = previous[0];
    previous[0] = firstIndex;

    for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
      const above = previous[secondIndex];
      previous[secondIndex] = first[firstIndex - 1] === second[secondIndex - 1]
        ? diagonal
        : Math.min(diagonal, above, previous[secondIndex - 1]) + 1;
      diagonal = above;
    }
  }

  return previous[second.length];
}

function isAdjacentTransposition(first: string, second: string) {
  if (first.length !== second.length) {
    return false;
  }

  const mismatches: number[] = [];
  for (let index = 0; index < first.length; index += 1) {
    if (first[index] !== second[index]) {
      mismatches.push(index);
      if (mismatches.length > 2) {
        return false;
      }
    }
  }

  return mismatches.length === 2
    && mismatches[1] === mismatches[0] + 1
    && first[mismatches[0]] === second[mismatches[1]]
    && first[mismatches[1]] === second[mismatches[0]];
}
